import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { NikLogo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useKids } from "@/context/KidsContext";
import { ChangePasswordModal } from "@/components/ChangePasswordModal";
import api from "@/lib/api";
import {
  Search, Bell, ChevronDown, LogOut, ShieldCheck, User as UserIcon, Menu, X, KeyRound,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { to: "/movies", label: "Movies", testid: "nav-movies-link" },
  { to: "/series", label: "Web Series", testid: "nav-series-link" },
  { to: "/originals", label: "Originals", testid: "nav-originals-link" },
  { to: "/kids", label: "Kids", testid: "nav-kids-link", kids: true },
  { to: "/live", label: "Live", testid: "nav-live-link" },
  { to: "/my-list", label: "My List", testid: "nav-mylist-link" },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [showChangePw, setShowChangePw] = useState(false);
  const { user, logout } = useAuth();
  const { enterKids } = useKids();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/search?q=${encodeURIComponent(q.trim())}`); setSearchOpen(false); }
  };

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <header
      data-testid="nikplay-header"
      className={`fixed top-0 left-0 right-0 z-50 px-4 md:px-12 py-3 transition-all duration-500 ${
        scrolled ? "bg-[#0B0E14]/90 backdrop-blur-xl border-b border-white/10" : "bg-gradient-to-b from-[#0B0E14]/95 to-transparent"
      }`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-8">
          <Link to="/" data-testid="nikplay-logo"><NikLogo /></Link>
          <nav className="hidden lg:flex items-center gap-6">
            {NAV.map((n) =>
              n.kids ? (
                <button
                  key={n.to}
                  onClick={enterKids}
                  data-testid={n.testid}
                  className="text-sm font-medium text-slate-400 transition-colors hover:text-white"
                >
                  {n.label}
                </button>
              ) : (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={n.testid}
                  className={`text-sm font-medium transition-colors hover:text-white ${
                    isActive(n.to) ? "text-white" : "text-slate-400"
                  }`}
                >
                  {n.label}
                </Link>
              )
            )}
          </nav>
        </div>

        <div className="flex items-center gap-3 md:gap-4">
          <form onSubmit={submitSearch} className="hidden sm:flex items-center">
            <div className={`flex items-center overflow-hidden transition-all duration-300 rounded-full border ${searchOpen ? "w-56 bg-[#121824]/90 border-white/20 px-3" : "w-9 border-transparent"}`}>
              <button type="button" data-testid="search-toggle-btn" onClick={() => setSearchOpen((s) => !s)} className="text-slate-200 hover:text-white shrink-0">
                <Search size={20} />
              </button>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="search-input"
                placeholder="Search titles..."
                className={`bg-transparent outline-none text-sm text-white ml-2 w-full ${searchOpen ? "block" : "hidden"}`}
              />
            </div>
          </form>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="notifications-btn" className="relative text-slate-200 hover:text-white">
                <Bell size={20} />
                {notifs.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-[var(--nik-blue)] text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center text-white">
                    {notifs.length}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 bg-[#121824] border-white/10 text-white">
              <div className="px-3 py-2 text-xs uppercase tracking-wider text-slate-400 font-medium">Notifications</div>
              {notifs.length === 0 && <div className="px-3 py-4 text-sm text-slate-400">No notifications</div>}
              {notifs.map((n) => (
                <div key={n.id} className="px-3 py-2.5 hover:bg-white/5 rounded-md">
                  <div className="text-sm font-semibold">{n.title}</div>
                  <div className="text-xs text-slate-400">{n.message}</div>
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="profile-menu-btn" className="flex items-center gap-1.5">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[var(--nik-blue)] to-[#0047B3] flex items-center justify-center text-sm font-bold overflow-hidden">
                    {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user.name?.[0] || "U").toUpperCase()}
                  </div>
                  <ChevronDown size={15} className="text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#121824] border-white/10 text-white">
                <div className="px-3 py-2">
                  <div className="text-sm font-semibold">{user.name}</div>
                  <div className="text-xs text-slate-400">{user.email}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-wider text-[var(--nik-gold)] font-bold">{user.subscription_plan} plan</div>
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="account-settings-link" onClick={() => navigate("/profile")} className="cursor-pointer focus:bg-white/10">
                  <UserIcon size={15} className="mr-2" /> Account Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/plans")} className="cursor-pointer focus:bg-white/10">
                  <UserIcon size={15} className="mr-2" /> Subscription
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="change-password-btn" onClick={() => setShowChangePw(true)} className="cursor-pointer focus:bg-white/10">
                  <KeyRound size={15} className="mr-2" /> Change Password
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="admin-link" onClick={() => navigate("/admin")} className="cursor-pointer focus:bg-white/10 text-[var(--nik-gold)]">
                    <ShieldCheck size={15} className="mr-2" /> KalamPrime Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="cursor-pointer focus:bg-white/10">
                  <LogOut size={15} className="mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button data-testid="header-signin-btn" onClick={() => navigate("/login")} className="bg-[var(--nik-blue)] hover:bg-[var(--nik-blue-hover)] text-white text-sm font-semibold px-4 py-1.5 rounded-full transition-colors">
              Sign In
            </button>
          )}

          <button className="lg:hidden text-white" onClick={() => setMobileOpen((s) => !s)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav className="lg:hidden mt-3 pb-2 flex flex-col gap-1 fade-up">
          {NAV.map((n) =>
            n.kids ? (
              <button key={n.to} onClick={() => { setMobileOpen(false); enterKids(); }}
                className="px-2 py-2 rounded-md text-sm text-slate-400 text-left">
                {n.label}
              </button>
            ) : (
              <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)}
                className={`px-2 py-2 rounded-md text-sm ${isActive(n.to) ? "text-white bg-white/5" : "text-slate-400"}`}>
                {n.label}
              </Link>
            )
          )}
          <form onSubmit={submitSearch} className="mt-1 flex items-center bg-[#121824] rounded-full px-3 py-2">
            <Search size={18} className="text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="bg-transparent outline-none text-sm text-white ml-2 w-full" />
          </form>
        </nav>
      )}
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
    </header>
  );
}
